import React, { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Download, Grid3X3, List, Table2, Video } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { api } from '@/lib/api/client'
import type { AdminStream } from '@/lib/shared-types/api/endpoints'
import { StreamRecording } from '@/components/admin/stream-recording'
import { AdminLayout } from '@/components/layouts/admin-layout'
import FilterBar from '@/components/common/filter-bar'
import { VirtualizedGrid } from '@/components/common/virtualized-grid'
import { StreamCard } from '@/components/admin/stream-card'
import { ADMIN_STREAM_FILTERS } from '@/lib/shared-types/filters'
import { PageHeader } from '@/components/page/page-header'
import { PageSection } from '@/components/page/page-section'
import { PageToolbar } from '@/components/page/page-toolbar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DrawerBuilder } from '@/components/common/drawer-builder'
import { Skeleton } from '@/components/ui/skeleton'
import { MediaImage } from '@/components/ui/media-image'

export default function AdminStreamsPage() {
    const [streams, setStreams] = useState<AdminStream[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [recordingDialogOpen, setRecordingDialogOpen] = useState(false)
    const [selectedStreamId, setSelectedStreamId] = useState<number | null>(null)
    const [selectedStreamForDrawer, setSelectedStreamForDrawer] = useState<AdminStream | null>(null)
    const [isStreamDrawerOpen, setIsStreamDrawerOpen] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [page, setPage] = useState(1)
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>('table')
    const navigate = useNavigate()

    // Filter state managed by FilterBar
    const [filters, setFilters] = useState<Record<string, string | string[]>>({})

    useEffect(() => {
        setPage(1);
        setHasMore(false);
    }, [filters]);

    useEffect(() => {
        fetchStreams()
    }, [page, filters]);

    const fetchStreams = useCallback(async () => {
        try {
            setIsLoading(true);
            setFetchError(null);
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: '20',
                ...filters
            });

            const response = await api.get<{ streams: AdminStream[]; pagination?: { hasMore: boolean } }>(`/api/admin/streams?${params.toString()}`)

            if (response.success && response.data) {
                const data = response.data;
                setStreams(prev => {
                    if (page === 1) {
                        return data.streams || [];
                    }
                    return [...prev, ...(data.streams || [])];
                });
                setHasMore(data.pagination?.hasMore ?? (data.streams || []).length === 20);
            } else {
                const msg = response.error?.message || 'Failed to load streams';
                setFetchError(msg);
                toast.error(msg);
            }
        } catch {
            const msg = 'Failed to load streams';
            setFetchError(msg);
            toast.error(msg);
        } finally {
            setIsLoading(false)
        }
    }, [page, filters]);

    const loadMore = () => {
        if (hasMore && !isLoading) {
            setPage(p => p + 1);
        }
    };

    const handleOpenStreamDrawer = (stream: AdminStream) => {
        setSelectedStreamForDrawer(stream)
        setIsStreamDrawerOpen(true)
    }

    const getStreamThumbnailUrl = (stream: AdminStream | null): string | undefined => {
        if (!stream) return undefined
        return stream.thumbnailUrl || (stream as AdminStream & { thumbnail_url?: string }).thumbnail_url
    }

    return (
        <AdminLayout>
            <PageHeader
                title="Streams"
                actions={(
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/admin/recordings')}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Recordings
                        </Button>
                        <Button size="sm" onClick={() => navigate('/admin/streams/new')}>
                            <Plus className="w-4 h-4 mr-2" />
                            New Stream
                        </Button>
                    </>
                )}
            />

            <div className="@container/page-toolbar">
                <PageToolbar
                    leading={(
                        <FilterBar
                            filters={ADMIN_STREAM_FILTERS}
                            activeFilters={filters}
                            onFilterChange={setFilters}
                            showFiltersLabel={false}
                        />
                    )}
                />
            </div>

            {fetchError ? (
                <Card>
                    <CardContent className="p-6 text-center">
                        <p className="text-destructive mb-3">{fetchError}</p>
                        <Button variant="outline" onClick={fetchStreams}>Try Again</Button>
                    </CardContent>
                </Card>
            ) : (
                <PageSection
                    asCard={false}
                    contentClassName="space-y-3"
                    actions={(
                        <div className="flex items-center gap-1 rounded-lg border border-border/70 bg-muted/30 p-1">
                            <Button
                                variant={viewMode === 'table' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                aria-label="Table view"
                            >
                                <Table2 className="h-4 w-4" />
                            </Button>
                            <Button
                                variant={viewMode === 'list' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('list')}
                                aria-label="List view"
                            >
                                <List className="h-4 w-4" />
                            </Button>
                            <Button
                                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                aria-label="Grid view"
                            >
                                <Grid3X3 className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                >
                    {viewMode === 'table' ? (
                        <div className="space-y-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Stream</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Scheduled</TableHead>
                                        <TableHead>Viewers</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading && streams.length === 0 ? (
                                        Array.from({ length: 6 }).map((_, index) => (
                                            <TableRow key={`loading-row-${index}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="h-10 w-16 shrink-0 rounded-md" />
                                                        <div className="min-w-0 space-y-1.5">
                                                            <Skeleton className="h-4 w-40" />
                                                            <Skeleton className="h-3 w-24" />
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Skeleton className="h-5 w-16 rounded-full" />
                                                </TableCell>
                                                <TableCell>
                                                    <Skeleton className="h-4 w-36" />
                                                </TableCell>
                                                <TableCell>
                                                    <Skeleton className="h-4 w-8" />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex justify-end gap-2">
                                                        <Skeleton className="h-8 w-16 rounded-md" />
                                                        <Skeleton className="h-8 w-20 rounded-md" />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : streams.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                                                No streams found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        streams.map((stream) => (
                                            <TableRow key={stream.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-900">
                                                            {getStreamThumbnailUrl(stream) ? (
                                                                <MediaImage src={getStreamThumbnailUrl(stream)} alt={stream.title} className="h-full w-full object-cover" />
                                                            ) : (
                                                                <div className="flex h-full items-center justify-center text-slate-600">
                                                                    <Video className="h-4 w-4" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenStreamDrawer(stream)}
                                                                className="truncate text-left font-medium text-primary hover:underline"
                                                            >
                                                                {stream.title}
                                                            </button>
                                                            <p className="text-xs text-muted-foreground">{stream.creator?.name || 'Unknown creator'}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={stream.status === 'live' ? 'destructive' : 'secondary'} className="uppercase">
                                                        {stream.status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {stream.scheduledStart ? new Date(stream.scheduledStart).toLocaleString() : 'Not scheduled'}
                                                </TableCell>
                                                <TableCell>{stream.viewerCount || 0}</TableCell>
                                                <TableCell>
                                                    <div className="flex justify-end gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/streams/${stream.id}`)}>
                                                            Manage
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedStreamId(stream.id);
                                                                setRecordingDialogOpen(true);
                                                            }}
                                                        >
                                                            Recording
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>

                            {hasMore && (
                                <div className="flex justify-center">
                                    <Button variant="outline" size="sm" onClick={loadMore} disabled={isLoading}>
                                        {isLoading ? 'Loading...' : 'Load more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-[560px] @[760px]/admin-layout:h-[600px]">
                            <VirtualizedGrid
                                items={streams}
                                renderItem={(stream) => (
                                    <StreamCard
                                        stream={stream}
                                        viewMode={viewMode}
                                        onRecordingClick={(id) => {
                                            setSelectedStreamId(id);
                                            setRecordingDialogOpen(true);
                                        }}
                                    />
                                )}
                                keyExtractor={(stream) => stream.id.toString()}
                                columns={{ sm: 1, md: 2, lg: 2, xl: 3, '2xl': 4 }}
                                gap="gap-4"
                                loading={isLoading}
                                hasNextPage={hasMore}
                                onLoadNextPage={loadMore}
                                loadMoreThreshold={5}
                                viewMode={viewMode}
                                itemHeight={viewMode === 'list' ? 120 : 320}
                                emptyMessage="No streams found"
                            />
                        </div>
                    )}
                </PageSection>
            )}

            <Dialog open={recordingDialogOpen} onOpenChange={setRecordingDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Recording Management</DialogTitle>
                        <DialogDescription>
                            Manage recordings for this stream
                        </DialogDescription>
                    </DialogHeader>
                    {selectedStreamId && <StreamRecording streamId={selectedStreamId} />}
                </DialogContent>
            </Dialog>

            <DrawerBuilder
                open={isStreamDrawerOpen}
                onOpenChange={setIsStreamDrawerOpen}
                title={selectedStreamForDrawer?.title || 'Stream details'}
                description="Quick stream overview"
            >
                {selectedStreamForDrawer && (
                    <div className="space-y-4">
                        <div className="overflow-hidden rounded-lg border border-border/70 bg-slate-900">
                            {getStreamThumbnailUrl(selectedStreamForDrawer) ? (
                                <MediaImage
                                    src={getStreamThumbnailUrl(selectedStreamForDrawer)}
                                    alt={selectedStreamForDrawer.title}
                                    className="h-44 w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-44 items-center justify-center text-slate-600">
                                    <Video className="h-8 w-8" />
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <Badge
                                variant={selectedStreamForDrawer.status === 'live' ? 'destructive' : 'secondary'}
                                className="uppercase"
                            >
                                {selectedStreamForDrawer.status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                                {selectedStreamForDrawer.viewerCount || 0} viewers
                            </span>
                        </div>

                        <div className="grid grid-cols-1 gap-3 text-sm">
                            <div>
                                <p className="text-muted-foreground">Creator</p>
                                <p className="font-medium">{selectedStreamForDrawer.creator?.name || 'Unknown creator'}</p>
                                <p className="text-muted-foreground">{selectedStreamForDrawer.creator?.email || 'No email'}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Scheduled</p>
                                <p className="font-medium">
                                    {selectedStreamForDrawer.scheduledStart
                                        ? new Date(selectedStreamForDrawer.scheduledStart).toLocaleString()
                                        : 'Not scheduled'}
                                </p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Created</p>
                                <p className="font-medium">
                                    {new Date(selectedStreamForDrawer.createdAt).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Comments</p>
                                <p className="font-medium">{selectedStreamForDrawer.commentCount ?? 0}</p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                className="flex-1"
                                onClick={() => {
                                    setIsStreamDrawerOpen(false)
                                    navigate(`/admin/streams/${selectedStreamForDrawer.id}`)
                                }}
                            >
                                Manage stream
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsStreamDrawerOpen(false)
                                    setSelectedStreamId(selectedStreamForDrawer.id)
                                    setRecordingDialogOpen(true)
                                }}
                            >
                                Recording
                            </Button>
                        </div>
                    </div>
                )}
            </DrawerBuilder>
        </AdminLayout>
    )
}
